import './AppLayout.scss';

import { memo, PropsWithChildren, useEffect, useState, useMemo } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import {
  Dropdown,
  DropdownItem,
  Icons,
  Menu,
  Notifications,
  Select,
  Spinner,
  TopBar,
} from 'plume-ui';
import { MenuStyles } from 'plume-ui/dist/components/Menu/Menu';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  activePartnerAtom,
  confirmationModalAtom,
  userAtom,
  usersPartnerListSelector,
} from '../../src/state';
import { DependencyContainer } from '../DependencyContainer';
import ConfirmationModal from '../components/ConfirmationModal/ConfirmationModal';
import { APP_URL, LOGIN_SCREEN_ROUTE, Routes } from '../config';
import useMonitorMetadata from '../features/Monitor/hooks/useMonitorMetadata';
import { useTrackEvent } from '../features/trackingAnalytics/hooks/useTrackEvent';
import { MixpanelService } from '../features/trackingAnalytics/services/mixPanel.service';
import useMenu from '../hooks/useMenu';
import { useApiVersion } from '../hooks/useVersion';
import { MixPanelEvents } from '../mixPanelEvents';
import { Maybe, Partner, Role, User } from '../types';
import FormattedMessage from '../utils/components/FormattedMessage';
import { isRhoRegionalSignal } from '../utils/environments';
import { useGetCurrentRoute } from '../utils/hooks/useGetCurrentRoute';
import { useRedirectToRoute } from '../utils/hooks/useRedirectToRoute';

const { loginService, introspectService } = new DependencyContainer();

const getUserNameForDropdown = (onlyinitials = false) => {
  const { firstName, lastName } = loginService.getUser();
  if (onlyinitials) return `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`;
  return `${firstName} ${lastName}`;
};
const getFullName = () => {
  const { firstName, lastName } = loginService.getUser();
  return `${firstName} ${lastName}`;
};

const mapRoleToDisplayNameTranslation: Record<Role, any> = {
  [Role.PlumeAdmin]: 'roleDisplayNames.plumeAdmin',
  [Role.PartnerAdmin]: 'roleDisplayNames.partnerAdmin',
  [Role.PlumeAdminReadOnly]: 'roleDisplayNames.plumeAdminReadOnly',
  [Role.PartnerSupportManagerInternal]:
    'roleDisplayNames.partnerSupportManagerInternal',
  [Role.PartnerSupportManagerExternal]:
    'roleDisplayNames.partnerSupportManagerExternal',
  [Role.PartnerSupportEngineer]: 'roleDisplayNames.partnerSupportEngineer',
};

const AppLayout = ({ children }: PropsWithChildren<{}>) => {
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();
  const { oktaAuth } = useOktaAuth();
  const getMenuItems = useMenu();
  const [introspect, setIntrospect] = useRecoilState(userAtom);
  const partners: Maybe<Partner[]> = useRecoilValue(usersPartnerListSelector);
  const [confirmationModal, setConfirmationModal] = useRecoilState(
    confirmationModalAtom,
  );
  useApiVersion();
  useMonitorMetadata();

  const setActivePartner = useSetRecoilState(activePartnerAtom);
  const activePartner = useRecoilValue(activePartnerAtom);
  const redirectToRoute = useRedirectToRoute();
  const { partnerId, monitorId, eventId } = useParams<{
    partnerId: string;
    monitorId: string;
    eventId: string;
  }>();
  const [query, setQuery] = useState<string>('');
  const getCurrentRoute = useGetCurrentRoute();
  const currentRoute = getCurrentRoute();

  const logout = async () => {
    loginService.removeAuthError();
    trackEvent({
      eventName: MixPanelEvents.LOGOUT,
    });
    await oktaAuth.signOut({
      postLogoutRedirectUri: APP_URL + LOGIN_SCREEN_ROUTE,
    });
  };

  const setPartner = (partner: Partner) => {
    setActivePartner(partner);
    MixpanelService.getInstance().setPartner(partner.name, partner.id || '');
    trackEvent({
      eventName: MixPanelEvents.PARTNER_CHANGE,
    });
    const routingParams = { partnerId: partner.id, monitorId, eventId };
    if (currentRoute === Routes.Index || partner.id !== partnerId) {
      if (currentRoute === Routes.Index) {
        redirectToRoute(Routes.HealthSummary, routingParams);
        return;
      }
      if (currentRoute === Routes.MonitorDetails) {
        redirectToRoute(Routes.Monitors, routingParams);
        return;
      }
      redirectToRoute(currentRoute, routingParams);
    }
  };

  const getSelectPartnerDropdownLabel = (): string => {
    if (activePartner) {
      return activePartner.name || 'Name missing';
    }
    return ' ';
  };

  useEffect(() => {
    introspectService
      .getIntrospect()
      .then((user) => {
        // @TODO: tmp solution, revisit types later on
        setIntrospect((user as unknown) as User);
      })
      .catch((error: any) => console.error('error getting introspect'));
  }, []);

  useEffect(() => {
    if (partners?.length) {
      const partnerLookup = partners.find((p) => p.id === partnerId);
      if (!partnerLookup) {
        if (partnerId) {
          console.error(
            `Partner ID requested not valid for this user: ${partnerId}`,
          );
        }
        setPartner(partners[0]);
      } else {
        setPartner(partnerLookup);
      }
    }
  }, [partners, partnerId]);

  const filteredPartners = useMemo(() => {
    if (!query.trim()) return partners;

    const lowerQuery = query.toLowerCase();
    return (
      partners?.filter(
        (partner: Partner) =>
          (partner.id && partner.id.toLowerCase().includes(lowerQuery)) ||
          partner.name.toLowerCase().includes(lowerQuery),
      ) || []
    );
  }, [partners, query]);

  const renderContent = () => {
    if (!introspect) {
      return (
        <div className="AppLayout__loading">
          <Spinner size="small" visible />
        </div>
      );
    }
    return (
      <div className="AppLayout__content" style={{ width: '100%' }}>
        {children}
      </div>
    );
  };
  const userDropdownItems = (
    username: string,
    email: string,
    role: Maybe<Role>,
  ) => (
    <div className="AppLayout__user--dropdown-wrapper">
      <div className="AppLayout__user--dropdown-wrapper-username">
        {username}
      </div>
      <div>{email}</div>
      <div>
        <FormattedMessage
          id={
            role
              ? mapRoleToDisplayNameTranslation[role]
              : 'roleDisplayNames.unsupported'
          }
        />
      </div>
    </div>
  );
  return (
    <div className="AppLayout">
      <TopBar
        brandingIcon={
          isRhoRegionalSignal() ? <Icons.JioIcon width={32} /> : undefined
        }
        userDropdown={{
          variant: 'withAvatar',
          username: getUserNameForDropdown(),
          badge: {
            size: 'standard',
            badgeContent: getUserNameForDropdown(true),
            hex: '#3C396F',
            hoverEffect: true,
          },
          signOutAction: () => logout(),
        }}
        userDropdownInfo={userDropdownItems(
          getFullName(),
          loginService.getUser().email,
          introspect?.role,
        )}
        rightSlotRenderer={() => (
          <Select
            noResultsContent={t('noResults')}
            placeholder={t('searchByName')}
            variant="borderless"
            maxItemsToShow={20}
            value={activePartner?.id || ''}
            maxItemsReachedMessage={t('maxResultsReached')}
            onSearch={setQuery}
            showSearch
            filterOption={false}
            options={
              filteredPartners?.map((partner) => ({
                label: partner.name,
                value: partner.id || '',
              })) || []
            }
            onChange={(value) => {
              const partner = partners?.find((p) => p.id === value);
              if (partner) {
                setPartner(partner);
                redirectToRoute(Routes.HealthSummary, {
                  partnerId: partner.id,
                });
              }
            }}
          />
        )}
        classes={(current) => ({
          ...current,
          root: `${current.root} AppLayout__top-bar`,
          centerSlot: `${current.centerSlot} AppLayout__top-bar-center-slot`,
        })}
      />
      <div className="AppLayout__row">
        <Menu
          classes={(current: MenuStyles) => ({
            ...current,
            root: `${current.root} AppLayout__menu`,
            wrapper: `${current.wrapper} AppLayout__menu--wrapper`,
            appName: `${current.appName} AppLayout__menu--appName`,
          })}
          groupedItems={getMenuItems()}
          appName="Signal"
        />
        {renderContent()}
      </div>
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onRequestClose={() =>
          setConfirmationModal((prev) => ({
            ...prev,
            isOpen: false,
          }))
        }
        onConfirm={() => confirmationModal.onConfirm?.()}
      />
      <Notifications />
    </div>
  );
};

export default memo(AppLayout);
